import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import './PromocodesPage.css';
import Pagination from '../components/Pagination';

interface PromocodeUsage {
  userId: string;
  userName: string | null;
  usedAt: string;
}

interface Promocode {
  _id: string;
  code: string;
  creditAmount: number;
  maxUseCount: number;
  usageCount: number;
  remainingUses: number;
  usagePercentage: number;
  usedBy: PromocodeUsage[];
  expiresAt: string;
  creditExpiryDays: number | null;
  isActive: boolean;
  isExpired: boolean;
  isExhausted: boolean;
  isAvailable: boolean;
  status: 'active' | 'expired' | 'exhausted' | 'inactive';
  note?: string;
  createdAt: string;
  updatedAt: string;
}

interface PromocodeStats {
  total: number;
  active: number;
  expired: number;
  exhausted: number;
  inactive: number;
  totalRedemptions: number;
  totalCreditsDistributed: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export default function PromocodesPage() {
  const [promocodes, setPromocodes] = useState<Promocode[]>([]);
  const [stats, setStats] = useState<PromocodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [detailPromo, setDetailPromo] = useState<Promocode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  
  // Generate dialog form state
  const [generateForm, setGenerateForm] = useState({
    count: 1,
    creditAmount: 100,
    maxUseCount: 1,
    daysUntilExpiry: 30,
    creditExpiryDays: null as number | null,
    note: ''
  });

  // Escape dismisses whichever dialog is open, same as the backdrop click —
  // except that an open note editor inside the detail modal cancels first, so
  // the key never discards the modal out from under an in-progress edit.
  useEscapeToClose(!!detailPromo, () => {
    if (editingNote) {
      if (!savingNote) setEditingNote(false);
      return;
    }
    setDetailPromo(null);
  });
  useEscapeToClose(showGenerateDialog, () => setShowGenerateDialog(false));

  const fetchStats = async () => {
    try {
      const response = await apiRequest<{
        success: boolean;
        stats: PromocodeStats;
      }>('/api/admin/promocodes/stats');
      
      if (response.success) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchPromocodes = async (page: number = 1, search: string = '', status: string = 'all') => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(search && { search }),
        ...(status !== 'all' && { status })
      });

      const response = await apiRequest<{
        success: boolean;
        data: Promocode[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/promocodes?${params}`);
      
      if (response.success) {
        setPromocodes(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to fetch promocodes');
      }
    } catch (err) {
      console.error('Error fetching promocodes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch promocodes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchPromocodes(currentPage, searchQuery, statusFilter);
  }, [currentPage, searchQuery, statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPromocodes(1, searchQuery, statusFilter);
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const response = await apiRequest<{
        success: boolean;
        message: string;
        isActive: boolean;
      }>(`/api/admin/promocodes/${id}/toggle`, {
        method: 'PUT'
      });
      
      if (response.success) {
        fetchPromocodes(currentPage, searchQuery, statusFilter);
        fetchStats();
      }
    } catch (err) {
      console.error('Error toggling promocode status:', err);
      alert('Failed to toggle promocode status');
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    
    try {
      const response = await apiRequest<{
        success: boolean;
        message: string;
        count: number;
        codes: string[];
        error?: string;
      }>('/api/admin/promocodes/generate', {
        method: 'POST',
        body: JSON.stringify(generateForm)
      });
      
      if (response.success) {
        alert(`Successfully generated ${response.count} promocodes!`);
        setShowGenerateDialog(false);
        setGenerateForm({
          count: 1,
          creditAmount: 100,
          maxUseCount: 1,
          daysUntilExpiry: 30,
          creditExpiryDays: null,
          note: ''
        });
        fetchPromocodes(1, '', 'all');
        fetchStats();
        setCurrentPage(1);
        setSearchQuery('');
        setStatusFilter('all');
      } else {
        alert(response.error || 'Failed to generate promocodes');
      }
    } catch (err) {
      console.error('Error generating promocodes:', err);
      alert('Failed to generate promocodes');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveNote = async () => {
    if (!detailPromo) return;
    setSavingNote(true);

    try {
      const response = await apiRequest<{
        success: boolean;
        note?: string;
        error?: string;
      }>(`/api/admin/promocodes/${detailPromo._id}/note`, {
        method: 'PUT',
        body: JSON.stringify({ note: noteValue })
      });

      if (response.success) {
        const updatedNote = noteValue.trim();
        setDetailPromo({ ...detailPromo, note: updatedNote });
        setPromocodes((prev) =>
          prev.map((p) => (p._id === detailPromo._id ? { ...p, note: updatedNote } : p))
        );
        setEditingNote(false);
      } else {
        alert(response.error || 'Failed to update note');
      }
    } catch (err) {
      console.error('Error updating note:', err);
      alert('Failed to update note');
    } finally {
      setSavingNote(false);
    }
  };

  const openDetails = (promo: Promocode) => {
    setDetailPromo(promo);
    setEditingNote(false);
    setNoteValue(promo.note || '');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading && !promocodes.length) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading promocodes...</p>
      </div>
    );
  }

  return (
    <div className="page promocodes-page">
      <div className="page-header">
        <div>
          <h1>Promocode Management</h1>
          <p className="page-subtitle">Manage and monitor promocode usage</p>
        </div>
        <button className="btn-primary" onClick={() => setShowGenerateDialog(true)}>
          + Generate Promocodes
        </button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total Codes</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-card stat-success">
            <div className="stat-label">Active</div>
            <div className="stat-value">{stats.active}</div>
          </div>
          <div className="stat-card stat-info">
            <div className="stat-label">Total Redemptions</div>
            <div className="stat-value">{stats.totalRedemptions}</div>
          </div>
          <div className="stat-card stat-warning">
            <div className="stat-label">Credits Distributed</div>
            <div className="stat-value">{stats.totalCreditsDistributed.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="exhausted">Exhausted</option>
            <option value="inactive">Inactive</option>
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Promocodes Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Credits</th>
              <th>Usage</th>
              <th>Status</th>
              <th>Note</th>
              <th>Expires</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {promocodes.map((promo) => (
              <tr key={promo._id}>
                <td data-label="Code">
                  <code className="code-text">{promo.code}</code>
                </td>
                <td data-label="Credits">{promo.creditAmount}</td>
                <td data-label="Usage">
                  <div className="usage-cell">
                    <span className="usage-text">
                      {promo.usageCount} / {promo.maxUseCount}
                    </span>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${promo.usagePercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </td>
                <td data-label="Status">
                  <span className={`status-badge status-${promo.status}`}>
                    {promo.status}
                  </span>
                </td>
                <td data-label="Note">
                  <span className="note-text">{promo.note || '-'}</span>
                </td>
                <td data-label="Expires" className="date-cell">{formatDate(promo.expiresAt)}</td>
                <td data-label="Actions">
                  <div className="action-buttons">
                    <button
                      onClick={() => openDetails(promo)}
                      className="btn-link"
                    >
                      Details
                    </button>
                    <button
                      onClick={() => handleToggleStatus(promo._id)}
                      className={`btn-link ${promo.isActive ? 'text-danger' : 'text-success'}`}
                    >
                      {promo.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.pages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}

      {/* Details Modal */}
      {detailPromo && (
        <div className="modal-overlay" onClick={() => setDetailPromo(null)}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <code className="code-text">{detailPromo.code}</code>
              </h2>
              <button className="modal-close" onClick={() => setDetailPromo(null)}>
                ×
              </button>
            </div>
            <div className="details-panel">
              <div className="details-section">
                <h4>Usage Details</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Credits:</span>
                    <span className="detail-value">{detailPromo.creditAmount}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Usage:</span>
                    <span className="detail-value">{detailPromo.usageCount} / {detailPromo.maxUseCount}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Remaining Uses:</span>
                    <span className="detail-value">{detailPromo.remainingUses}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value">
                      <span className={`status-badge status-${detailPromo.status}`}>{detailPromo.status}</span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Credit Expiry:</span>
                    <span className="detail-value">
                      {detailPromo.creditExpiryDays
                        ? `${detailPromo.creditExpiryDays} days after redemption`
                        : 'Default (end of next year)'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Expires:</span>
                    <span className="detail-value">{formatDate(detailPromo.expiresAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">{formatDate(detailPromo.createdAt)}</span>
                  </div>
                </div>
              </div>
              <div className="details-section">
                <div className="details-section-header">
                  <h4>Note</h4>
                  {!editingNote && (
                    <button
                      className="btn-link"
                      onClick={() => {
                        setNoteValue(detailPromo.note || '');
                        setEditingNote(true);
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingNote ? (
                  <div className="note-edit">
                    <textarea
                      value={noteValue}
                      onChange={(e) => setNoteValue(e.target.value)}
                      placeholder="Add a description or note for this code..."
                      className="form-textarea"
                      rows={3}
                      autoFocus
                    />
                    <div className="note-edit-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setEditingNote(false)}
                        disabled={savingNote}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleSaveNote}
                        disabled={savingNote}
                      >
                        {savingNote ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="note-display">{detailPromo.note || 'No note'}</p>
                )}
              </div>
              {detailPromo.usedBy.length > 0 && (
                <div className="details-section">
                  <h4>Redeemed By ({detailPromo.usedBy.length})</h4>
                  <div className="usage-list">
                    <table className="usage-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>User ID</th>
                          <th>Redeemed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailPromo.usedBy.map((usage, idx) => (
                          <tr key={idx}>
                            <td>{usage.userName || '-'}</td>
                            <td><code>{usage.userId}</code></td>
                            <td>{formatDate(usage.usedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generate Dialog */}
      {showGenerateDialog && (
        <div className="modal-overlay" onClick={() => setShowGenerateDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generate Promocodes</h2>
              <button className="modal-close" onClick={() => setShowGenerateDialog(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleGenerate} className="modal-form">
              <div className="form-group">
                <label>Number of Codes</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={generateForm.count}
                  onChange={(e) => setGenerateForm({ ...generateForm, count: parseInt(e.target.value) })}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Credit Amount</label>
                <input
                  type="number"
                  min="1"
                  value={generateForm.creditAmount}
                  onChange={(e) => setGenerateForm({ ...generateForm, creditAmount: parseInt(e.target.value) })}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Max Use Count</label>
                <input
                  type="number"
                  min="1"
                  value={generateForm.maxUseCount}
                  onChange={(e) => setGenerateForm({ ...generateForm, maxUseCount: parseInt(e.target.value) })}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Days Until Code Expiry</label>
                <input
                  type="number"
                  min="1"
                  value={generateForm.daysUntilExpiry}
                  onChange={(e) => setGenerateForm({ ...generateForm, daysUntilExpiry: parseInt(e.target.value) })}
                  required
                  className="form-input"
                />
                <span className="form-hint">How long the code can be redeemed</span>
              </div>
              <div className="form-group">
                <label>Credit Expiry Days (Optional)</label>
                <input
                  type="number"
                  min="1"
                  value={generateForm.creditExpiryDays ?? ''}
                  onChange={(e) => setGenerateForm({
                    ...generateForm,
                    creditExpiryDays: e.target.value ? parseInt(e.target.value) : null
                  })}
                  placeholder="Default (end of next year)"
                  className="form-input"
                />
                <span className="form-hint">Days until credits expire after redemption (leave empty for default)</span>
              </div>
              <div className="form-group">
                <label>Note (Optional)</label>
                <textarea
                  value={generateForm.note}
                  onChange={(e) => setGenerateForm({ ...generateForm, note: e.target.value })}
                  placeholder="Add a description or note for these codes..."
                  className="form-textarea"
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowGenerateDialog(false)}
                  className="btn-secondary"
                  disabled={generating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
